import { useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, X, FolderPlus, CheckCircle2 } from "lucide-react";
import { useEscapeKey } from "@/hooks/useEscapeKey";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

const COUNTRIES = [
  { code: "IND", label: "India", currency: "INR" },
  { code: "USA", label: "USA", currency: "USD" },
  { code: "UAE", label: "UAE", currency: "AED" },
  { code: "SINGAPORE", label: "Singapore", currency: "SGD" },
  { code: "EUROPE", label: "Europe", currency: "EUR" },
  { code: "UK", label: "UK", currency: "GBP" },
];

const EMPTY = { name: "", address: "", gst_applicable: false, gst_number: "", poc_name: "", poc_phone: "", poc_email: "", country: "" };

/** E3 (2026-02 founder decision): the streamlined "New Client" intake form — captures more
 * than the generic client editor (Address, GST, Point of Contact, Country->Currency), auto-
 * creates a linked Contact from the POC fields, and offers "Add New Project" as the next step. */
export function NewClientForm({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // { client, contact }
  useEscapeKey(onClose, open);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCountry = COUNTRIES.find((c) => c.code === form.country);

  const close = () => { setForm(EMPTY); setCreated(null); onClose(); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), address: form.address || undefined,
        gst_applicable: form.gst_applicable, gst_number: form.gst_applicable ? form.gst_number : undefined,
        country: form.country || undefined,
        poc_name: form.poc_name || undefined, poc_phone: form.poc_phone || undefined, poc_email: form.poc_email || undefined,
      };
      const r = await api.post("/v1/clients/", payload);
      toast.success(`${form.name} created`);
      if (form.poc_name && !r.data.contact?.ok) {
        toast.error("Client saved, but the linked Contact couldn't be created - add it manually from Contacts");
      } else if (form.poc_name) {
        toast.success(`Contact "${form.poc_name}" linked to ${form.name}`);
      }
      setCreated(r.data.record);
      onCreated?.(r.data.record);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="new-client-form-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-6 shadow-2xl">
        <button onClick={close} data-testid="new-client-form-close" className="absolute top-4 right-4 text-[#A8A29E] hover:text-[#1C1C1E]"><X className="h-4 w-4" /></button>

        {created ? (
          <div className="text-center py-6" data-testid="new-client-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-editorial text-xl">{created.name} created</h3>
            <p className="text-sm text-[#57534E] mt-1">What's next?</p>
            <div className="mt-5 flex flex-col gap-2">
              <button data-testid="add-new-project-cta"
                onClick={() => { onCreated?.(created, true); close(); }}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#FFC817] px-4 py-2.5 text-sm font-medium text-[#1C1C1E] hover:bg-[#E6B415]">
                <FolderPlus className="h-4 w-4" /> Add New Project
              </button>
              <button onClick={close} data-testid="new-client-done" className="rounded-md border border-[#E7E5E4] px-4 py-2.5 text-sm text-[#57534E] hover:text-[#1C1C1E]">Done</button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-editorial text-xl tracking-tight">New Client</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Client Name *</label>
                <input data-testid="new-client-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Corp" />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input data-testid="new-client-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, City" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Country</label>
                  <select data-testid="new-client-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)}>
                    <option value="">Select...</option>
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Billing Currency</label>
                  <input disabled data-testid="new-client-currency" className={`${inputCls} opacity-70`} value={selectedCountry?.currency || "-"} readOnly />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input type="checkbox" id="gst-applicable" data-testid="new-client-gst-toggle" checked={form.gst_applicable} onChange={(e) => set("gst_applicable", e.target.checked)} className="h-4 w-4 accent-[#FFC817]" />
                <label htmlFor="gst-applicable" className="text-sm text-[#1C1C1E]">GST Applicable?</label>
              </div>
              {form.gst_applicable && (
                <div>
                  <label className={labelCls}>GST Number</label>
                  <input data-testid="new-client-gst-number" className={inputCls} value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} placeholder="22AAAAA0000A1Z5" />
                </div>
              )}
              <div className="pt-2 border-t border-[#E7E5E4]">
                <div className={labelCls}>Point of Contact</div>
                <input data-testid="new-client-poc-name" className={`${inputCls} mt-2`} value={form.poc_name} onChange={(e) => set("poc_name", e.target.value)} placeholder="Contact name" />
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <input data-testid="new-client-poc-phone" className={inputCls} value={form.poc_phone} onChange={(e) => set("poc_phone", e.target.value)} placeholder="Phone" />
                  <input data-testid="new-client-poc-email" className={inputCls} value={form.poc_email} onChange={(e) => set("poc_email", e.target.value)} placeholder="Email" />
                </div>
                {form.poc_name && <div className="text-[10px] text-[#A8A29E] mt-1.5">A linked Contact will be created automatically from these details.</div>}
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={close} className="flex-1 rounded-md border border-[#E7E5E4] px-4 py-2.5 text-sm text-[#57534E] hover:text-[#1C1C1E]">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="new-client-save" className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-[#FFC817] px-4 py-2.5 text-sm font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
